/**
 * TipTap marks that anchor comment/suggestion threads to document text.
 *
 * The thread id travels INSIDE the document content (mark attribute), so
 * anchors move naturally with edits, persist through the block rows and sync
 * to collaborators. The thread discussion itself lives in a sidecar block
 * (`@folio/shared` → comments/model.ts) keyed by the same id.
 */
import { Mark, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

export const COMMENT_MARK = "comment";
export const SUGGESTION_INSERT_MARK = "suggestionInsert";
export const SUGGESTION_DELETE_MARK = "suggestionDelete";

/** Attribute name carrying the thread id, per mark. */
export function markIdAttribute(markName: string): string {
  return markName === COMMENT_MARK ? "threadId" : "suggestionId";
}

export interface CommentMarkOptions {
  /** Clicking commented text focuses its thread in the panel. */
  onCommentClick?: (threadId: string) => void;
}

export const CommentMark = Mark.create<CommentMarkOptions>({
  name: COMMENT_MARK,
  // Typing at the edge of a comment should not silently extend it.
  inclusive: false,
  keepOnSplit: true,

  addOptions() {
    return { onCommentClick: undefined };
  },

  addAttributes() {
    return {
      threadId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-comment-id"),
        renderHTML: (attributes: { threadId?: string | null }) =>
          attributes.threadId ? { "data-comment-id": attributes.threadId } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-comment-id]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { class: "comment-mark" }), 0];
  },

  addProseMirrorPlugins() {
    const onCommentClick = this.options.onCommentClick;
    if (!onCommentClick) {
      return [];
    }
    return [
      new Plugin({
        key: new PluginKey("comment-mark-click"),
        props: {
          handleClick: (_view, _pos, event) => {
            const target = event.target instanceof Element ? event.target : null;
            const id = target?.closest("[data-comment-id]")?.getAttribute("data-comment-id");
            if (id) {
              onCommentClick(id);
            }
            return false;
          },
        },
      }),
    ];
  },
});

export const SuggestionInsertMark = Mark.create({
  name: SUGGESTION_INSERT_MARK,
  // Inclusive: continued typing extends the pending insertion run.
  inclusive: true,
  keepOnSplit: true,

  addAttributes() {
    return {
      suggestionId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-suggestion-insert"),
        renderHTML: (attributes: { suggestionId?: string | null }) =>
          attributes.suggestionId ? { "data-suggestion-insert": attributes.suggestionId } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-suggestion-insert]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { class: "suggestion-insert" }), 0];
  },
});

export const SuggestionDeleteMark = Mark.create({
  name: SUGGESTION_DELETE_MARK,
  inclusive: false,
  keepOnSplit: true,

  addAttributes() {
    return {
      suggestionId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-suggestion-delete"),
        renderHTML: (attributes: { suggestionId?: string | null }) =>
          attributes.suggestionId ? { "data-suggestion-delete": attributes.suggestionId } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-suggestion-delete]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { class: "suggestion-delete" }), 0];
  },
});
